import { useId, useState } from 'react';
import { Check, Eye, LockKeyhole, Minus } from 'lucide-react';
import {
  Badge,
  Button,
  Checkbox,
  Disclosure,
  EmptyState,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import {
  accessError,
  changeGrant,
  visibleTo,
  type AccessAction,
  type AccessGrants,
  type AccessRole,
  type RelationState,
} from './relationData';
export function ObjectVisibility({
  roles,
  grants,
  viewAction = 'view',
}: {
  roles: readonly AccessRole[];
  grants: AccessGrants;
  viewAction?: string;
}) {
  const viewers = visibleTo(roles, grants, viewAction);
  return (
    <div data-slot="object-visibility" className="rounded-lg border border-line bg-muted px-4 py-3">
      <Disclosure
        title={
          <span className="inline-flex items-center gap-2 text-sm">
            <Eye className="size-4 text-fg-subtle" />
            {viewers.length
              ? 'Видят объект: ' + viewers.map((role) => role.label).join(', ')
              : 'Ни одной роли не выдан просмотр'}
          </span>
        }
      >
        <div className="space-y-2 pt-2">
          {viewers.map((role) => (
            <div key={role.id} className="flex flex-wrap items-baseline gap-2">
              <Badge>{role.label}</Badge>
              {role.description && <span className="text-xs text-fg-muted">{role.description}</span>}
            </div>
          ))}
          <p className="text-xs text-fg-muted">
            Показаны явно переданные права. Наследование и проверка доступа выполняются сервисом.
          </p>
        </div>
      </Disclosure>
    </div>
  );
}
export function AccessMatrix({
  roles,
  actions,
  grants,
  onChange,
  readOnly = false,
  state = 'ready',
  onRetry,
}: {
  roles: readonly AccessRole[];
  actions: readonly AccessAction[];
  grants: AccessGrants;
  onChange?: (next: AccessGrants) => void | Promise<void>;
  readOnly?: boolean;
  state?: RelationState;
  onRetry?: () => void;
}) {
  const id = useId(),
    [pending, setPending] = useState(false),
    [error, setError] = useState('');
  const invalid = accessError(roles, actions, grants);
  async function change(roleId: string, actionId: string, enabled: boolean) {
    if (!onChange || pending) return;
    setError('');
    setPending(true);
    try {
      await onChange(changeGrant(grants, roleId, actionId, enabled));
    } catch {
      setError('Права не сохранены. Прежние значения сохранены; повтори действие.');
    } finally {
      setPending(false);
    }
  }
  return (
    <section data-slot="access-matrix" aria-labelledby={id} className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 id={id} className="type-heading">
            Роли и действия
          </h3>
          <p className="mt-1 text-xs text-fg-muted">
            Явные разрешения для объекта; пустая клетка означает, что право не выдано.
          </p>
        </div>
        <Badge>
          <LockKeyhole className="size-3" /> {readOnly ? 'Только просмотр' : 'Настройка прав'}
        </Badge>
      </div>
      {state === 'loading' ? (
        <div aria-label="Загрузка прав" className="space-y-2">
          {[0, 1, 2, 3].map((n) => (
            <Skeleton key={n} className="h-10 w-full" />
          ))}
        </div>
      ) : state === 'error' || invalid ? (
        <EmptyState
          title="Права недоступны"
          icon={<LockKeyhole />}
          description={invalid ?? 'Не удалось загрузить права.'}
          action={onRetry ? { label: 'Повторить', onClick: onRetry } : undefined}
        />
      ) : !roles.length || !actions.length ? (
        <EmptyState title="Роли или действия не заданы" icon={<LockKeyhole />} />
      ) : (
        <div className="overflow-hidden rounded-lg border border-line">
          <Table
            aria-label="Матрица прав роли на действия"
            viewportProps={{
              tabIndex: 0,
              role: 'region',
              'aria-label': 'Прокрутка матрицы прав',
            }}
          >
            <TableHeader>
              <TableRow>
                <TableHead>Роль</TableHead>
                {actions.map((action) => (
                  <TableHead key={action.id} className="text-center whitespace-nowrap">
                    {action.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.map((role) => (
                <TableRow key={role.id}>
                  <TableCell>
                    <p className="font-medium whitespace-nowrap">{role.label}</p>
                    {role.description && <p className="mt-1 text-xs text-fg-muted">{role.description}</p>}
                  </TableCell>
                  {actions.map((action) => {
                    const enabled = !!grants[role.id]?.includes(action.id);
                    return (
                      <TableCell key={action.id} className="text-center">
                        {readOnly || !onChange ? (
                          <span aria-label={enabled ? 'Разрешено' : 'Не выдано'} className="inline-flex">
                            {enabled ? (
                              <Check className="size-4 text-success-strong" />
                            ) : (
                              <Minus className="size-4 text-fg-subtle" />
                            )}
                          </span>
                        ) : (
                          <span className="inline-flex">
                            <Checkbox
                              aria-label={role.label + ': ' + action.label}
                              checked={enabled}
                              disabled={pending}
                              onCheckedChange={(value) => void change(role.id, action.id, value === true)}
                            />
                          </span>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {pending && (
        <p role="status" className="text-xs text-fg-muted">
          Сохранение разрешения…
        </p>
      )}
      {error && (
        <div className="flex flex-wrap items-center gap-2">
          <p role="alert" className="text-sm text-danger-strong">
            {error}
          </p>
          <Button variant="ghost" size="sm" onClick={() => setError('')}>
            Закрыть сообщение
          </Button>
        </div>
      )}
    </section>
  );
}
